import { FC, useState, useEffect } from 'react'
import * as yup from 'yup'
import { Formik, FormikValues } from 'formik'
import { useSelector } from 'react-redux'
import CustomRadio from '../UI/CustomRadio/CustomRadio'
import styles from './BookingForm.module.css'
import CustomInput from '../UI/CustomInput/CustomInput'
import CustomButton from '../UI/CustomButton/CustomButton'
import Loader from '../UI/Loader/Loader'
import { saveBookingFormData } from '../../redux/reducers/bookingTabs/bookingTabsReducer'
import { createClaimPublic } from '../../redux/actions/claimsAction'
import { RootState } from '../../redux/store'
import { useAppSelector, useAppDispatch } from '../../redux/types/types'
import { getDaDataCompany } from '../../redux/actions/daDataAction'
import { clearDadata } from '../../redux/reducers/daData/daDataSlice'
import { useDebounce } from '../../hooks/useDebounce'
import { useToast } from '../../context/ToastContext'

interface BookingFormProps {
  handleNextTab: () => void
}

const BookingForm: FC<BookingFormProps> = ({ handleNextTab }) => {
  const bookingData = useSelector((state: RootState) => state.bookingTabs.bookingData)
  const { items: publicOrderItems } = useAppSelector((state) => state.publicOrder)
  const { daDataCompany } = useAppSelector((state) => state.daData)
  const dispatch = useAppDispatch()
  const { showError } = useToast()

  const [inn, setInn] = useState<string>('')
  const [loader, setLoader] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [bookingType, setBookingType] = useState<string>('Физическое лицо')
  const [submittedValues, setSubmittedValues] = useState<{
    organizationName: string
    address: string
    city: string
    kpp: string
    ogrn: string
    contactPerson: string
    phone: string
  } | null>(null)
  const debounced = useDebounce(inn, 1000)

  const handleInnInputChange = async (value: string) => {
    const numericValue = value.replace(/\D/g, '')
    setInn(numericValue)
  }

  const validationSchema = yup.object().shape({
    bookingType: yup.string().required('Поле является обязательным'),
    fullName: yup.string().when('bookingType', {
      is: (val: string) => val === 'Физическое лицо',
      then: (schema) => schema.required('ФИО является обязательным'),
      otherwise: (schema) => schema.notRequired(),
    }),
    phone: yup.string().required('Номер телефона является обязательным'),
    email: yup.string().email('Неверный формат email').required('Email является обязательным'),
    organizationName: yup.string().when('bookingType', {
      is: (val: string) => val === 'Юридическое лицо',
      then: (schema) => schema.required('Название организации является обязательным'),
      otherwise: (schema) => schema.notRequired(),
    }),
    inn: yup.string().when('bookingType', {
      is: (val: string) => val === 'Юридическое лицо',
      then: (schema) => schema.required('ИНН является обязательным'),
      otherwise: (schema) => schema.notRequired(),
    }),
    kpp: yup.string().when('bookingType', {
      is: (val: string) => val === 'Юридическое лицо',
      then: (schema) => schema.required('КПП является обязательным'),
      otherwise: (schema) => schema.notRequired(),
    }),
    ogrn: yup.string().when('bookingType', {
      is: (val: string) => val === 'Юридическое лицо',
      then: (schema) => schema.required('ОГРН является обязательным'),
      otherwise: (schema) => schema.notRequired(),
    }),
    city: yup.string().when('bookingType', {
      is: (val: string) => val === 'Юридическое лицо',
      then: (schema) => schema.required('Город является обязательным'),
      otherwise: (schema) => schema.notRequired(),
    }),
    address: yup.string().when('bookingType', {
      is: (val: string) => val === 'Юридическое лицо',
      then: (schema) => schema.required('Адрес является обязательным'),
      otherwise: (schema) => schema.notRequired(),
    }),
    contactPerson: yup.string().when('bookingType', {
      is: (val: string) => val === 'Юридическое лицо',
      then: (schema) => schema.required('ФИО контактного лица является обязательным'),
      otherwise: (schema) => schema.notRequired(),
    }),
  })

  useEffect(() => {
    if (inn.length === 0 || inn.length < 10) {
      setSubmittedValues(null)
      dispatch(clearDadata())
    }
  }, [inn, dispatch])

  useEffect(() => {
    if (debounced && debounced.length >= 10) {
      setLoader(true)

      dispatch(getDaDataCompany({ query: debounced }))

      const timer = setTimeout(() => {
        setLoader(false)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [debounced, dispatch])

  useEffect(() => {
    if (daDataCompany?.suggestions && daDataCompany.suggestions.length > 0 && inn.length >= 10) {
      const currentData = daDataCompany.suggestions[0]?.data
      if (currentData && (currentData.name?.full || currentData.address?.value)) {
        const newValues = {
          organizationName: currentData.name?.full || '',
          address: currentData.address?.value || '',
          city: currentData.address?.data?.city || '',
          kpp: currentData.kpp || '',
          ogrn: currentData.ogrn || '',
          contactPerson: currentData.management?.name || '',
          phone: currentData.phones || '',
        }
        const hasChanged =
          !submittedValues ||
          submittedValues.organizationName !== newValues.organizationName ||
          submittedValues.address !== newValues.address ||
          submittedValues.city !== newValues.city ||
          submittedValues.kpp !== newValues.kpp ||
          submittedValues.ogrn !== newValues.ogrn ||
          submittedValues.contactPerson !== newValues.contactPerson ||
          submittedValues.phone !== newValues.phone

        if (hasChanged) {
          setSubmittedValues(newValues)
        }
      }
    }
  }, [daDataCompany, inn, submittedValues])

  const handleSubmitHandler = async (values: FormikValues) => {
    try {
      setIsSubmitting(true)

      dispatch(
        saveBookingFormData({
          bookingType: values.bookingType,
          fullName: values.fullName,
          phone: values.phone,
          email: values.email,
          organizationName: values.organizationName,
          inn: values.inn,
          kpp: values.kpp,
          ogrn: values.ogrn,
          city: values.city,
          address: values.address,
          contactPerson: values.contactPerson,
        }),
      )

      if (!bookingData) {
        console.error('Booking data is missing')
        setIsSubmitting(false)
        return
      }

      if (!bookingData.placeId) {
        console.error('Place ID is missing')
        setIsSubmitting(false)
        return
      }

      const resultAction = await dispatch(
        createClaimPublic({
          publicClientData: values,
          products: publicOrderItems,
          placeId: bookingData.placeId,
          peopleCount: bookingData.peopleCount,
          startDate: bookingData.startDate,
          endDate: bookingData.endDate,
        }),
      )

      if (createClaimPublic.rejected.match(resultAction)) {
        showError('Произошла ошибка при создании бронирования')
      } else {
        handleNextTab()
      }
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }
  if (loader || isSubmitting) {
    return <Loader />
  }

  return (
    <div className={styles.bookingForm}>
      <div className={styles.bookingFormHeader}>
        <h1>Укажите свои данные</h1>
      </div>
      <div className={styles.bookingFormContent}>
        <Formik
          key={submittedValues ? 'with-data' : 'without-data'}
          initialValues={{
            bookingType: bookingType,
            fullName: '',
            phone: submittedValues?.phone || '',
            email: '',
            organizationName: submittedValues?.organizationName || '',
            inn: inn,
            kpp: submittedValues?.kpp || '',
            ogrn: submittedValues?.ogrn || '',
            city: submittedValues?.city || '',
            address: submittedValues?.address || '',
            contactPerson: submittedValues?.contactPerson || '',
          }}
          validateOnBlur
          onSubmit={(values) => handleSubmitHandler(values)}
          validationSchema={validationSchema}
          enableReinitialize={true}
        >
          {(props) => {
            const { values, touched, errors, handleChange, handleSubmit, setFieldValue } = props
            const isIndividual = values.bookingType === 'Физическое лицо'

            const handleBookingTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              const newBookingType = e.target.value
              setBookingType(newBookingType)
              handleChange(e)
              if (newBookingType === 'Физическое лицо') {
                setInn('')
                setSubmittedValues(null)
                dispatch(clearDadata())
                setFieldValue('organizationName', '')
                setFieldValue('inn', '')
                setFieldValue('kpp', '')
                setFieldValue('ogrn', '')
                setFieldValue('city', '')
                setFieldValue('address', '')
                setFieldValue('contactPerson', '')
              }
            }

            return (
              <form className={styles.bookingFormContentForm} onSubmit={handleSubmit}>
                <div className={styles.bookingFormContentItem}>
                  <div className={styles.bookingFormContentItemTitle}>Я бронирую как</div>
                  <div className={styles.bookingFormContentItemInput}>
                    <CustomRadio
                      options={['Физическое лицо', 'Юридическое лицо']}
                      name='bookingType'
                      onChange={handleBookingTypeChange}
                      value={values.bookingType}
                    />
                  </div>
                  {touched.bookingType && errors.bookingType && (
                    <p className={styles.errorValidation}>{errors.bookingType}</p>
                  )}
                </div>

                {isIndividual && (
                  <div className={styles.bookingFormContentItem}>
                    <div className={styles.bookingFormContentItemTitle}>ФИО</div>
                    <div className={styles.bookingFormContentItemInput}>
                      <CustomInput
                        placeholder='Введите'
                        type='text'
                        name='fullName'
                        onChange={handleChange}
                        value={values.fullName}
                      />
                      {touched.fullName && errors.fullName && (
                        <p className={styles.errorValidation}>{errors.fullName}</p>
                      )}
                    </div>
                  </div>
                )}

                {!isIndividual && (
                  <>
                    <div className={styles.bookingFormContentItem}>
                      <div className={styles.bookingFormContentItemTitle}>Название организации</div>
                      <div className={styles.bookingFormContentItemInput}>
                        <CustomInput
                          placeholder='Введите'
                          type='text'
                          name='organizationName'
                          onChange={handleChange}
                          value={values.organizationName}
                        />
                        {touched.organizationName && errors.organizationName && (
                          <p className={styles.errorValidation}>{errors.organizationName}</p>
                        )}
                      </div>
                    </div>

                    <div className={styles.bookingFormContentItem}>
                      <div className={styles.bookingFormContentItemTitle}>ИНН</div>
                      <div className={styles.bookingFormContentItemInput}>
                        <div className={styles.inputContainerWithLoader}>
                          <CustomInput
                            placeholder='Введите'
                            type='text'
                            name='inn'
                            onChange={(e) => {
                              handleInnInputChange(e.target.value)
                              handleChange(e)
                            }}
                            value={inn}
                          />
                          {loader && inn.length >= 10 && (
                            <div className={styles.loader}>Загрузка...</div>
                          )}
                          {inn.length > 0 &&
                            inn.length >= 10 &&
                            daDataCompany?.suggestions &&
                            daDataCompany.suggestions.length > 0 && (
                              <div className={styles.successMessage}>
                                ✓ Данные организации загружены
                              </div>
                            )}
                          {inn.length > 0 &&
                            inn.length >= 10 &&
                            (!daDataCompany?.suggestions ||
                              daDataCompany.suggestions.length === 0) &&
                            !loader && (
                              <div className={styles.errorContainer}>
                                <p className={styles.errorValidation}>
                                  Организация с таким ИНН не найдена
                                </p>
                              </div>
                            )}
                        </div>
                        {touched.inn && errors.inn && (
                          <p className={styles.errorValidation}>{errors.inn as string}</p>
                        )}
                      </div>
                    </div>

                    <div className={styles.bookingFormContentItem}>
                      <div className={styles.bookingFormContentItemTitle}>КПП</div>
                      <div className={styles.bookingFormContentItemInput}>
                        <CustomInput
                          placeholder='Введите'
                          type='text'
                          name='kpp'
                          onChange={handleChange}
                          value={values.kpp}
                        />
                        {touched.kpp && errors.kpp && (
                          <p className={styles.errorValidation}>{errors.kpp as string}</p>
                        )}
                      </div>
                    </div>

                    <div className={styles.bookingFormContentItem}>
                      <div className={styles.bookingFormContentItemTitle}>ОГРН</div>
                      <div className={styles.bookingFormContentItemInput}>
                        <CustomInput
                          placeholder='Введите'
                          type='text'
                          name='ogrn'
                          onChange={handleChange}
                          value={values.ogrn}
                        />
                        {touched.ogrn && errors.ogrn && (
                          <p className={styles.errorValidation}>{errors.ogrn as string}</p>
                        )}
                      </div>
                    </div>

                    <div className={styles.bookingFormContentItem}>
                      <div className={styles.bookingFormContentItemTitle}>Город</div>
                      <div className={styles.bookingFormContentItemInput}>
                        <CustomInput
                          placeholder='Введите'
                          type='text'
                          name='city'
                          onChange={handleChange}
                          value={values.city}
                        />
                        {touched.city && errors.city && (
                          <p className={styles.errorValidation}>{errors.city as string}</p>
                        )}
                      </div>
                    </div>

                    <div className={styles.bookingFormContentItem}>
                      <div className={styles.bookingFormContentItemTitle}>Адрес</div>
                      <div className={styles.bookingFormContentItemInput}>
                        <CustomInput
                          placeholder='Введите'
                          type='text'
                          name='address'
                          onChange={handleChange}
                          value={values.address}
                        />
                        {touched.address && errors.address && (
                          <p className={styles.errorValidation}>{errors.address as string}</p>
                        )}
                      </div>
                    </div>

                    <div className={styles.bookingFormContentItem}>
                      <div className={styles.bookingFormContentItemTitle}>ФИО контактного лица</div>
                      <div className={styles.bookingFormContentItemInput}>
                        <CustomInput
                          placeholder='Введите'
                          type='text'
                          name='contactPerson'
                          onChange={handleChange}
                          value={values.contactPerson}
                        />
                        {touched.contactPerson && errors.contactPerson && (
                          <p className={styles.errorValidation}>{errors.contactPerson as string}</p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className={styles.bookingFormContentItem}>
                  <div className={styles.bookingFormContentItemTitle}>Номер телефона</div>
                  <div className={styles.bookingFormContentItemInput}>
                    <CustomInput
                      placeholder='Введите'
                      type='text'
                      name='phone'
                      onChange={handleChange}
                      value={values.phone}
                    />
                    {touched.phone && errors.phone && (
                      <p className={styles.errorValidation}>{errors.phone as string}</p>
                    )}
                  </div>
                </div>

                <div className={styles.bookingFormContentItem}>
                  <div className={styles.bookingFormContentItemTitle}>Эл. почта</div>
                  <div className={styles.bookingFormContentItemInput}>
                    <CustomInput
                      placeholder='Введите'
                      type='text'
                      name='email'
                      onChange={handleChange}
                      value={values.email}
                    />
                    {touched.email && errors.email && (
                      <p className={styles.errorValidation}>{errors.email as string}</p>
                    )}
                  </div>
                </div>

                <div className={styles.buttonContainer}>
                  <CustomButton
                    className={styles.submitBtn}
                    title={isSubmitting ? 'Отправка...' : 'Отправить'}
                    callbackHandler={handleSubmit}
                    disabled={isSubmitting}
                  />
                </div>
              </form>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}

export default BookingForm
